/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Private Endpoints Dashboard
 * Feature #140: Network Topology & Security Dashboard - US3
 * Constitution: v1.12.0
 * Conformed to Spec 197 design blueprint
 */

import React, { useState, useMemo } from 'react';
import {
  Lock, CheckCircle2, Clock, XCircle, RefreshCw, Search,
} from 'lucide-react';
import { useNetworkSummary, usePrivateEndpoints } from '../../services/operations/networkService';
import { useDebounce } from '../../hooks/useDebounce';
import { ExportButton } from '../../components/operations';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import type { PrivateEndpoint } from '../../types/network';
import { NoAccessBadge, WorkerVisibilityNotice } from '../../components/collection/WorkerVisibility';

// ============================================================================
// Helpers
// ============================================================================

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger'> = {
  Approved: 'success',
  Pending: 'warning',
  Rejected: 'danger',
  Disconnected: 'danger',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  Approved: <CheckCircle2 className="h-3 w-3" />,
  Pending: <Clock className="h-3 w-3" />,
  Rejected: <XCircle className="h-3 w-3" />,
  Disconnected: <XCircle className="h-3 w-3" />,
};

const ConnectionStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge variant={STATUS_VARIANT[status] ?? 'secondary'} leftIcon={STATUS_ICON[status]}>
    {status}
  </Badge>
);

// ============================================================================
// Component
// ============================================================================

export const PrivateEndpointsPage: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<string>('');
  const [targetResourceType, setTargetResourceType] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const debouncedSearch = useDebounce(search, 300);

  const filters = useMemo(() => {
    const f: Record<string, unknown> = {};
    if (connectionStatus) f.connectionStatus = connectionStatus;
    if (targetResourceType) f.targetResourceType = targetResourceType;
    if (debouncedSearch) f.search = debouncedSearch;
    return f;
  }, [connectionStatus, targetResourceType, debouncedSearch]);

  const summaryQuery = useNetworkSummary();
  const endpointsQuery = usePrivateEndpoints(filters);
  const summary = summaryQuery.data;
  const endpoints = endpointsQuery.data ?? [];

  const targetTypes = useMemo(
    () => [...new Set(endpoints.map((e) => e.target_resource_type).filter(Boolean) as string[])].sort(),
    [endpoints],
  );

  const isInitialLoading = summaryQuery.isLoading && endpointsQuery.isLoading;
  const errorMessage = summaryQuery.error || endpointsQuery.error
    ? 'Failed to load private endpoint data.'
    : null;

  const clearFilters = () => {
    setConnectionStatus('');
    setTargetResourceType('');
    setSearch('');
  };

  const refetchAll = () => {
    summaryQuery.refetch();
    endpointsQuery.refetch();
  };

  const columns: DataTableColumn<PrivateEndpoint>[] = [
    {
      key: 'endpoint_name',
      header: 'Name',
      width: '15%',
      render: (pe) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="font-medium text-[var(--color-text-primary)]">{pe.endpoint_name}</span>
          {pe.no_access_since && <NoAccessBadge since={pe.no_access_since} />}
        </span>
      ),
    },
    { key: 'resource_group', header: 'Resource Group', width: '13%' },
    {
      key: 'subnet_name',
      header: 'Subnet',
      width: '11%',
      render: (pe) => pe.subnet_name ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'vnet_name',
      header: 'VNet',
      width: '11%',
      render: (pe) => pe.vnet_name ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'location',
      header: 'Location',
      width: '9%',
      render: (pe) => pe.location ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'target_resource',
      header: 'Target Resource',
      width: '13%',
      render: (pe) => (
        <span title={pe.target_resource_id ?? ''}>
          {pe.target_sub_resource ?? pe.target_resource_id?.split('/').pop() ?? '—'}
        </span>
      ),
    },
    {
      key: 'target_resource_type',
      header: 'Target Type',
      width: '11%',
      render: (pe) =>
        pe.target_resource_type
          ? pe.target_resource_type.split('/').pop()
          : <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'connection_status',
      header: 'Status',
      width: '9%',
      render: (pe) => <ConnectionStatusBadge status={pe.connection_status} />,
    },
    {
      key: 'private_ip_address',
      header: 'Private IP',
      width: '8%',
      render: (pe) => (
        <span className="font-mono text-[var(--color-text-secondary)]">{pe.private_ip_address ?? '—'}</span>
      ),
    },
  ];

  return (
    <PageLayout
      helpId="auto.private-endpoints"
      title="Private Endpoints"
      description="Cross-subscription private endpoint inventory and connection status"
      icon={<Lock className="h-6 w-6" />}
      actions={
        <>
          <Button variant="outline" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={refetchAll}>
            Refresh
          </Button>
          <ExportButton
            data={endpoints}
            filename="private-endpoints"
            columns={[
              { key: 'endpoint_name', label: 'Name' },
              { key: 'resource_group', label: 'Resource Group' },
              { key: 'subnet_name', label: 'Subnet' },
              { key: 'vnet_name', label: 'VNet' },
              { key: 'target_resource_type', label: 'Target Type' },
              { key: 'target_sub_resource', label: 'Target Sub-Resource' },
              { key: 'connection_status', label: 'Status' },
              { key: 'private_ip_address', label: 'Private IP' },
              { key: 'provisioning_state', label: 'Provisioning State' },
              { key: 'location', label: 'Location' },
              { key: 'azure_resource_id', label: 'Azure Resource ID' },
              { key: 'snapshot_date', label: 'Last Updated' },
            ]}
          />
        </>
      }
    >
      {errorMessage ? (
        <ErrorState title="Failed to load private endpoints" message={errorMessage} onRetry={refetchAll} />
      ) : isInitialLoading ? (
        <LoadingState rows={6} />
      ) : (
        <>
          {summary && (
            <WorkerVisibilityNotice
              since={summary.noAccessSince}
              counts={[{ noun: 'private endpoints', noAccess: summary.noAccessPrivateEndpoints, total: summary.totalPrivateEndpoints }]}
            />
          )}
          {/* KPI tiles */}
          {summary && (
            <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
              <StatCard
                label="Total"
                value={summary.totalPrivateEndpoints}
                icon={<Lock className="h-4 w-4 text-[var(--color-accent)]" />}
              />
              <StatCard
                label="Approved"
                value={summary.approvedEndpoints}
                valueClassName="text-[var(--color-status-success)]"
                icon={<CheckCircle2 className="h-4 w-4 text-[var(--color-status-success)]" />}
              />
              <StatCard
                label="Pending"
                value={summary.pendingEndpoints}
                valueClassName="text-[var(--color-status-warning)]"
                icon={<Clock className="h-4 w-4 text-[var(--color-status-warning)]" />}
              />
              <StatCard
                label="Rejected"
                value={summary.rejectedEndpoints}
                valueClassName="text-[var(--color-status-danger)]"
                icon={<XCircle className="h-4 w-4 text-[var(--color-status-danger)]" />}
              />
              {/* Spec 286 T159 item 25: Azure's fourth connection state; without it the tiles did not add up to Total. */}
              <StatCard
                label="Disconnected"
                value={summary.disconnectedEndpoints}
                valueClassName="text-[var(--color-status-danger)]"
                icon={<XCircle className="h-4 w-4 text-[var(--color-status-danger)]" />}
              />
            </div>
          )}

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full details.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Filters */}
          <Card className="mb-6 p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
              <div className="md:col-span-5">
                <Input
                  isFullWidth
                  label="Search"
                  placeholder="Search by name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  leftAddon={<Search className="h-4 w-4" />}
                />
              </div>
              <div className="md:col-span-3">
                <Select
                  isFullWidth
                  label="Connection Status"
                  value={connectionStatus}
                  onChange={(e) => setConnectionStatus(e.target.value)}
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'Approved', label: 'Approved' },
                    { value: 'Pending', label: 'Pending' },
                    { value: 'Rejected', label: 'Rejected' },
                    { value: 'Disconnected', label: 'Disconnected' },
                  ]}
                />
              </div>
              <div className="md:col-span-3">
                <Select
                  isFullWidth
                  label="Target Resource Type"
                  value={targetResourceType}
                  onChange={(e) => setTargetResourceType(e.target.value)}
                  options={[
                    { value: '', label: 'All Types' },
                    ...targetTypes.map((t) => ({ value: t, label: t.split('/').pop() ?? t })),
                  ]}
                />
              </div>
              <div className="md:col-span-1 flex md:justify-end">
                <Button variant="secondary" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </Card>

          {/* Endpoints table */}
          <Card padding="none">
            {endpointsQuery.isLoading ? (
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            ) : (
              <DataTable
                columns={columns}
                rows={endpoints}
                rowKey={(pe) => pe.id}
                minWidth="64rem"
                emptyMessage="No network data collected yet."
              />
            )}
          </Card>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default PrivateEndpointsPage;
